import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export interface SlotCapacityTrackSegment {
  id: string;
  startPercent: number;
  widthPercent: number;
  availableCount: number;
  occupiedCount: number;
  totalCount: number;
  status: string;
  past: boolean;
  hardUnavailable: boolean;
  title?: string | null;
}

export interface SlotCapacityPastOverlaySegment {
  id: string;
  startPercent: number;
  widthPercent: number;
  fadeStartPercent?: number | null;
}

@Component({
  selector: 'lib-slot-capacity-track',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (segments.length > 0) {
      <div class="slot-capacity-track" aria-hidden="true">
        @for (segment of segments; track segment.id) {
          <div
            class="slot-capacity-track__segment"
            [class.slot-capacity-track__segment--empty]="segment.totalCount > 0 && segment.availableCount === 0"
            [class.slot-capacity-track__segment--full]="segment.totalCount > 0 && segment.availableCount === segment.totalCount"
            [class.slot-capacity-track__segment--past]="segment.past"
            [class.slot-capacity-track__segment--hard-unavailable]="segment.hardUnavailable"
            [style.left.%]="segment.startPercent"
            [style.width.%]="segment.widthPercent"
            [style.--slot-capacity-color]="capacityColor(segment)"
            [style.--slot-next-capacity-color]="nextCapacityColor(segment)"
            [title]="segment.title ?? ''"
          ></div>
        }
        @if (hasPastSegments) {
          <div
            class="slot-capacity-track__past-overlay"
            [style.mask-image]="pastMaskImage"
          ></div>
        }
      </div>
    }
  `,
  styles: [`
    .slot-capacity-track {
      position: absolute;
      inset: 0;
      overflow: hidden;
      border: 1px solid #bfdbfe;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.68);
      pointer-events: none;
    }

    .slot-capacity-track__segment {
      position: absolute;
      top: 0;
      bottom: 0;
      min-width: 1px;
      background:
        linear-gradient(
          90deg,
          var(--slot-capacity-color, var(--tenant-slot-unavailable, #f3f4f6)) 0%,
          var(--slot-capacity-color, var(--tenant-slot-unavailable, #f3f4f6)) 85%,
          var(--slot-next-capacity-color, var(--slot-capacity-color, var(--tenant-slot-unavailable, #f3f4f6))) 100%
        ),
        transparent;
    }

    .slot-capacity-track__past-overlay {
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      mask-repeat: no-repeat;
      mask-size: 100% 100%;
      background:
        repeating-linear-gradient(
          135deg,
          color-mix(
            in srgb,
            var(--tenant-slot-past-pattern, #64748b) var(--tenant-slot-past-pattern-opacity, 20%),
            transparent
          ) 0,
          color-mix(
            in srgb,
            var(--tenant-slot-past-pattern, #64748b) var(--tenant-slot-past-pattern-opacity, 20%),
            transparent
          ) 6px,
          rgba(255, 255, 255, 0) 6px,
          rgba(255, 255, 255, 0) 12px
        );
    }
  `],
})
export class SlotCapacityTrackComponent {
  @Input() public segments: SlotCapacityTrackSegment[] = [];
  @Input() public pastSegments: SlotCapacityPastOverlaySegment[] = [];

  protected get hasPastSegments(): boolean {
    return this.pastSegments.length > 0;
  }

  protected get pastMaskImage(): string {
    return this.pastSegments
      .map((segment) => {
        const start = this.clampPercent(segment.startPercent);
        const end = this.clampPercent(segment.startPercent + segment.widthPercent);
        const fadeStart = this.clampPercent(segment.fadeStartPercent ?? end);
        return `linear-gradient(90deg, transparent 0%, transparent ${start}%, #000 ${start}%, #000 ${fadeStart}%, transparent ${end}%, transparent 100%)`;
      })
      .join(', ');
  }

  protected capacityColor(segment: SlotCapacityTrackSegment): string {
    return this.capacityColorFor(segment);
  }

  protected nextCapacityColor(segment: SlotCapacityTrackSegment): string {
    return this.capacityColorFor(this.nextSegment(segment) ?? segment);
  }

  private nextSegment(segment: SlotCapacityTrackSegment): SlotCapacityTrackSegment | null {
    const index = this.segments.findIndex((item) => item.id === segment.id);
    return index >= 0 ? this.segments[index + 1] ?? null : null;
  }

  private capacityColorFor(segment: SlotCapacityTrackSegment): string {
    if (segment.hardUnavailable || segment.totalCount <= 0) {
      return 'var(--tenant-slot-unavailable, #f3f4f6)';
    }

    const ratio = Math.max(0, Math.min(1, segment.availableCount / segment.totalCount));
    if (ratio <= 0.5) {
      const pressureShare = Math.round(ratio * 200 * 1000) / 1000;
      return `color-mix(in srgb, var(--tenant-slot-pressure, #f59e0b) ${pressureShare}%, var(--tenant-slot-unavailable, #f3f4f6) ${100 - pressureShare}%)`;
    }

    const availableShare = Math.round((ratio - 0.5) * 200 * 1000) / 1000;
    return `color-mix(in srgb, var(--tenant-slot-available, #22c55e) ${availableShare}%, var(--tenant-slot-pressure, #f59e0b) ${100 - availableShare}%)`;
  }

  private clampPercent(value: number): number {
    return Math.max(0, Math.min(100, Math.round(value * 1000) / 1000));
  }
}
